"use client";

import { useRef, useState } from "react";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Separator, Sheet, SheetContent, SheetTitle, SheetTrigger } from "@akashnetwork/ui/components";
import { cn, REMOVE_SCROLL_CLASS_NAMES } from "@akashnetwork/ui/utils";
import { ChevronDown, Cloud, Files, LayoutGrid, Menu, Server } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ActivityBell } from "@src/components/activity/ActivityBell/ActivityBell";
import { CommandMenu } from "@src/components/command-menu/CommandMenu/CommandMenu";
import { SkipOnboardingButton } from "@src/components/onboarding-picker/SkipOnboardingButton/SkipOnboardingButton";
import { CreateOrganizationDialog } from "@src/components/organizations/CreateOrganizationDialog/CreateOrganizationDialog";
import { OrganizationSwitcher } from "@src/components/organizations/OrganizationSwitcher/OrganizationSwitcher";
import { useOpenWhatsNew } from "@src/components/whats-new/useOpenWhatsNew";
import { WhatsNewAutoOpen } from "@src/components/whats-new/WhatsNewAutoOpen/WhatsNewAutoOpen";
import { useActiveOrganization } from "@src/context/OrganizationProvider/OrganizationProvider";
import { useFlag } from "@src/hooks/useFlag";
import { useSettingsNavLinks } from "@src/hooks/useSettingsNavLinks";
import useCookieTheme from "@src/hooks/useTheme";
import { useUser } from "@src/hooks/useUser";
import { isRouteActive } from "@src/utils/isRouteActive";
import { UrlService } from "@src/utils/urlUtils";
import { AkashLogo } from "../AkashLogo";
import { HackathonCouponNavEntry } from "../HackathonCouponNavEntry/HackathonCouponNavEntry";
import { TopBanner } from "../TopBanner";
import { usePublishHeaderHeight } from "../usePublishHeaderHeight";
import { TopNavAccountMenu } from "./TopNavAccountMenu";
export const DEPENDENCIES = {
  useUser,
  usePathname,
  useCookieTheme,
  useFlag,
  useActiveOrganization,
  useOpenWhatsNew,
  TopBanner,
  HackathonCouponNavEntry,
  CommandMenu,
  ActivityBell,
  TopNavAccountMenu,
  SkipOnboardingButton,
  OrganizationSwitcher,
  CreateOrganizationDialog,
  WhatsNewAutoOpen
};
interface Props {
  dependencies?: typeof DEPENDENCIES;
  /** During onboarding, reduce the chrome to the logo and a logout-only account menu. */
  minimal?: boolean;
}
type TopNavLink = {
  title: string;
  url: string;
  isActive: boolean;
  icon: React.ComponentType<{
    className?: string;
  }>;
};
export function TopNav({
  dependencies: d = DEPENDENCIES,
  minimal = false
}: Props = {}) {
  const theme = d.useCookieTheme();
  const headerRef = useRef<HTMLElement>(null);
  usePublishHeaderHeight(headerRef);
  const {
    user
  } = d.useUser();
  const isAuthenticated = !!user?.userId;
  const pathname = d.usePathname();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const isOrganizationsEnabled = d.useFlag("organizations");
  const [isCreateOrganizationOpen, setIsCreateOrganizationOpen] = useState(false);
  const openCreateOrganization = isOrganizationsEnabled ? () => setIsCreateOrganizationOpen(true) : undefined;
  const openWhatsNew = d.useOpenWhatsNew();
  const replayWhatsNew = isOrganizationsEnabled ? () => openWhatsNew("account_menu") : undefined;
  const {
    projectsEnabled
  } = d.useActiveOrganization();
  const projectsLink: TopNavLink[] = isOrganizationsEnabled && projectsEnabled ? [{
    title: "Projects",
    url: UrlService.projects(),
    isActive: isRouteActive(pathname, "/projects"),
    icon: LayoutGrid
  }] : [];
  const navLinks: TopNavLink[] = [...projectsLink, {
    title: "Deployments",
    url: UrlService.deploymentList(),
    isActive: isRouteActive(pathname, "/deployments", "/new-deployment"),
    icon: Cloud
  }, {
    title: "Providers",
    url: UrlService.providers(),
    isActive: isRouteActive(pathname, "/providers"),
    icon: Server
  }, {
    title: "Templates",
    url: UrlService.templates(),
    isActive: isRouteActive(pathname, "/templates"),
    icon: Files
  }];
  const settingsLinks = useSettingsNavLinks({
    dependencies: {
      usePathname: d.usePathname,
      useFlag: d.useFlag,
      useActiveOrganization: d.useActiveOrganization
    }
  });
  const isSettingsActive = settingsLinks.some(link => link.isActive);
  const showNavLinks = isAuthenticated && !minimal;
  const isCommandMenuEnabled = d.useFlag("ui_command_menu");
  const showOrganizationSwitcher = isAuthenticated && !!openCreateOrganization;
  const createOrganizationFromSwitcher = minimal ? undefined : openCreateOrganization;
  return <header ref={headerRef} className={cn("fixed left-0 right-0 top-0 z-50 border-b border-border bg-header", REMOVE_SCROLL_CLASS_NAMES.zeroRight)} data-sentry-component="TopNav" data-sentry-source-file="TopNav.tsx">
      <d.TopBanner data-sentry-element="d.TopBanner" data-sentry-source-file="TopNav.tsx" />

      <div className="flex h-14 items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-8">
          {!!theme && <Link className="flex items-center" href={UrlService.home()} aria-label="Akash Console home">
              <AkashLogo />
            </Link>}

          {showNavLinks && <nav aria-label="Primary" className="hidden items-center gap-1 md:flex">
              {navLinks.map(link => <Link key={link.title} href={link.url} aria-current={link.isActive ? "page" : undefined} className={desktopNavLinkClasses(link.isActive)}>
                  {link.title}
                </Link>)}

              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className={cn(desktopNavLinkClasses(isSettingsActive), "flex items-center gap-1")}>
                  Settings
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[160px]">
                  {settingsLinks.map(link => <DropdownMenuItem key={link.title} asChild className="cursor-pointer">
                      <Link href={link.url}>{link.title}</Link>
                    </DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>}
        </div>

        <div className="flex items-center gap-2.5">
          {!minimal && <div className="hidden md:block">
              <d.HackathonCouponNavEntry />
            </div>}

          {minimal && <d.SkipOnboardingButton source="auto_deploy" />}

          {showNavLinks && isCommandMenuEnabled && <d.CommandMenu />}

          {showNavLinks && <d.ActivityBell />}

          {showOrganizationSwitcher && <d.OrganizationSwitcher onCreateOrganization={createOrganizationFromSwitcher} />}

          <div className={cn({
          "hidden md:block": showNavLinks
        })}>
            <d.TopNavAccountMenu minimal={minimal} onCreateOrganization={openCreateOrganization} onOpenWhatsNew={replayWhatsNew} data-sentry-element="d.TopNavAccountMenu" data-sentry-source-file="TopNav.tsx" />
          </div>

          {showNavLinks && <Sheet open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="ghost" className="rounded-full md:hidden" aria-label="Open navigation menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0">
                <div className="flex h-14 items-center border-b border-border px-4">
                  <SheetTitle asChild>
                    <Link href={UrlService.home()} aria-label="Akash Console home" className="flex items-center" onClick={() => setIsMobileNavOpen(false)}>
                      <AkashLogo />
                    </Link>
                  </SheetTitle>
                </div>
                {showOrganizationSwitcher && <div className="border-b border-border p-3 empty:hidden">
                    <d.OrganizationSwitcher layout="sheet" onCreateOrganization={openCreateOrganization} onNavigate={() => setIsMobileNavOpen(false)} />
                  </div>}
                <nav aria-label="Primary mobile" className="flex flex-col gap-1 p-3">
                  {navLinks.map(link => <Link key={link.title} href={link.url} aria-current={link.isActive ? "page" : undefined} className={mobileNavLinkClasses(link.isActive)} onClick={() => setIsMobileNavOpen(false)}>
                      <link.icon className="h-5 w-5 shrink-0" />
                      {link.title}
                    </Link>)}

                  <Separator className="my-2" />

                  <div className="px-3 py-1 text-xs font-medium uppercase text-muted-foreground">Settings</div>
                  {settingsLinks.map(link => <Link key={link.title} href={link.url} aria-current={link.isActive ? "page" : undefined} className={mobileNavLinkClasses(link.isActive)} onClick={() => setIsMobileNavOpen(false)}>
                      <link.icon className="h-5 w-5 shrink-0" />
                      {link.title}
                    </Link>)}

                  <Separator className="my-2" />

                  <d.TopNavAccountMenu variant="inline" onNavigate={() => setIsMobileNavOpen(false)} onCreateOrganization={openCreateOrganization} onOpenWhatsNew={replayWhatsNew} />
                </nav>
              </SheetContent>
            </Sheet>}
        </div>
      </div>

      {isCreateOrganizationOpen && <d.CreateOrganizationDialog onClose={() => setIsCreateOrganizationOpen(false)} />}

      {showOrganizationSwitcher && !minimal && <d.WhatsNewAutoOpen />}
    </header>;
}
function desktopNavLinkClasses(isActive: boolean) {
  return cn("rounded-md px-3 py-2 text-sm transition-colors hover:text-foreground", {
    "font-medium text-foreground": isActive,
    "text-muted-foreground": !isActive
  });
}
function mobileNavLinkClasses(isActive: boolean) {
  return cn("flex items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-accent", {
    "bg-accent font-medium text-foreground": isActive,
    "text-muted-foreground": !isActive
  });
}